import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import AddBox from '@material-ui/icons/AddBox';
import Clear from '@material-ui/icons/Clear';
import DeleteIcon from '@material-ui/icons/Delete';
import { keyBy } from 'lodash';
import React, { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import {
  SortableContainer as sortableContainer,
  SortableElement as sortableElement,
  SortableElementProps,
  SortableHandle as sortableHandle,
} from 'react-sortable-hoc';
import titleize from 'titleize';
import { VerticalEllipsisIcon } from '../../assets/Svg';
import { IPopularKeywordDto, IKeyword, ILateNightKeyword } from '../../services/types';
import KeywordManagement from './KeywordManagement';
import useStyles from './styles';
import { SubmitControl } from '../../components/SubmitControl';
import useSnackbar from '../../hooks/useSnackbar';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import {
  getTopSearchKeywords,
  getPopularKeywords,
  getLateNightKeywords,
  addLateNightKeyword,
  deleteLateNightKeywordById,
  savePopularKeywords,
} from '../../services/api/Search';

interface IProps {}

interface IKeywordCellProps {
  keywordObject: IKeyword;
  border?: boolean;
  isEditing?: boolean;
  isDraggable?: boolean;
  isBlockedToSelect?: boolean;
  isDisabled?: boolean;
  onCheck?: (event: ChangeEvent<HTMLInputElement>) => void;
}

const SearchFeature = (): JSX.Element => {
  const classes = useStyles();

  const [topSearchKeywordsDictionary, setTopSearchKeywordsDictionary] = useState<Record<string, IKeyword>>({});
  const [duration, setDuration] = useState<number>(7);
  const [addKeywordInputValue, setAddKeywordInputValue] = useState<string>('');
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [popularKeywords, setPopularKeywords] = useState<IKeyword[]>([]);

  const [pristinePopularKeywords, setPristinePopularKeywords] = useState<IKeyword[]>([]); // FIXME: use "useRef" instead of "useState"

  const snackbar = useSnackbar();
  const confirm = useConfirmDialog();

  const handleClickLastDayWithDuration = (duration: number) => async () => {
    setDuration(duration);
    const keywordDictionary = await fetchTopSearchKeywordsWithDuration(duration);

    if (popularKeywords) {
      popularKeywords.forEach(({ keyword }) => (keywordDictionary[keyword] ? (keywordDictionary[keyword].isSelected = true) : undefined));
    }

    setTopSearchKeywordsDictionary(keywordDictionary);
  };

  const fetchTopSearchKeywordsWithDuration = async (duration: number) => {
    setIsFetching(true);
    let keywordDictionary: Record<string, IKeyword> = {};

    try {
      const response = await getTopSearchKeywords(duration);
      const result = response.data.keyword;

      result
        .filter(({ keyword }) => !!keyword)
        .forEach((result, index) => {
          result.keyword = titleize(result.keyword);
          result.isSelected = false;
          result.rank = index + 1;
        });

      keywordDictionary = keyBy(result, 'keyword');
      setIsFetching(false);
    } catch (e) {
      console.error('error', e); // FIXME: Proper error handling.
      setIsFetching(false);
    }

    return keywordDictionary;
  };

  const fetchPopularKeywords = async () => {
    let keywords: IKeyword[] = [];

    try {
      const response = await getPopularKeywords();

      keywords = response.data.map((keyword, index) => {
        return { keyword, rank: index + 1, isSelected: false };
      });
    } catch (e) {
      console.error('error', e); // FIXME: Proper error handling.
    }

    return keywords;
  };

  useEffect(() => {
    const initialize = async () => {
      const [keywords, searchKeywordDictionary] = await Promise.all([fetchPopularKeywords(), fetchTopSearchKeywordsWithDuration(7)]);

      if (keywords) {
        keywords.forEach(({ keyword }) => (searchKeywordDictionary[keyword] ? (searchKeywordDictionary[keyword].isSelected = true) : undefined));
      }

      setPopularKeywords(keywords);
      setTopSearchKeywordsDictionary(searchKeywordDictionary);
    };

    initialize();
  }, []);

  const addPopularKeyword = (newKeyword: string) => {
    if (popularKeywords.find(({ keyword }) => keyword.toLowerCase() === newKeyword.toLowerCase())) {
      return;
    }

    const clonedPopularKeywords = [...popularKeywords];
    const newKeywordObject: IKeyword = {
      keyword: newKeyword,
      isSelected: false,
      rank: clonedPopularKeywords.length + 1,
    };

    clonedPopularKeywords.push(newKeywordObject);
    setPopularKeywords(clonedPopularKeywords);

    if (topSearchKeywordsDictionary[newKeyword]) {
      const clonedTopSearchKeywordsDictionary = { ...topSearchKeywordsDictionary };

      clonedTopSearchKeywordsDictionary[newKeyword].isSelected = true;
      setTopSearchKeywordsDictionary(clonedTopSearchKeywordsDictionary);
    }

    setAddKeywordInputValue('');
  };

  const handleChangeAddKeywordInput = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event || !event.target) {
      return;
    }

    setAddKeywordInputValue(event.target.value);
  };

  const handleKeyPressAddKeywordInput = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addPopularKeyword(addKeywordInputValue);
    }
  };

  const handleClickClearAddKeywordInput = () => {
    setAddKeywordInputValue('');
  };

  const handleClickEditButton = () => {
    setPristinePopularKeywords([...popularKeywords]);

    setIsEditing(true);
  };

  const handleClickCancelEditButton = () => {
    const clonedTopSearchKeywordsDictionary = { ...topSearchKeywordsDictionary };

    pristinePopularKeywords.forEach(({ keyword }) =>
      clonedTopSearchKeywordsDictionary[keyword] ? (clonedTopSearchKeywordsDictionary[keyword].isSelected = true) : undefined,
    );

    rearrangeRankingOfPopularKeywords(pristinePopularKeywords);
    setPopularKeywords(pristinePopularKeywords);
    setPristinePopularKeywords([]);
    setTopSearchKeywordsDictionary(clonedTopSearchKeywordsDictionary);
    setIsEditing(false);
  };

  const handleClickApplyButton = () => {
    const onProceed = async () => {
      const requestDtos: IPopularKeywordDto[] = popularKeywords.map(({ keyword }, index) => {
        return {
          keyword,
          order: index + 1,
        };
      });

      try {
        setIsSaving(true);
        await savePopularKeywords(requestDtos);

        snackbar.success('Applied successfully.');
      } catch (e) {
        snackbar.error('An error has occurred.');
      }

      setIsSaving(false);
      setIsEditing(false);
    };

    confirm.open({
      onProceed,
    });
  };

  const handleClickDeleteButton = () => {
    const clonedTopSearchKeywordsDictionary = { ...topSearchKeywordsDictionary };

    const remainingPopularKeywords = popularKeywords.filter(({ keyword, isSelected }) => {
      if (isSelected && clonedTopSearchKeywordsDictionary[keyword]) {
        clonedTopSearchKeywordsDictionary[keyword].isSelected = false;
      }

      return !isSelected;
    });

    rearrangeRankingOfPopularKeywords(remainingPopularKeywords);
    setPopularKeywords(remainingPopularKeywords);
    setTopSearchKeywordsDictionary(clonedTopSearchKeywordsDictionary);
  };

  const handleChangeTopSearchKeywordCheckbox = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event || !event.target) {
      return;
    }

    const key = event.target.value;
    const clonedTopSearchKeywordsDictionary = { ...topSearchKeywordsDictionary };
    const targetKeyword = clonedTopSearchKeywordsDictionary[key] as IKeyword;

    if (targetKeyword) {
      const currentSelection = targetKeyword.isSelected;
      targetKeyword.isSelected = !currentSelection;
    }

    setTopSearchKeywordsDictionary(clonedTopSearchKeywordsDictionary);

    const clonedPopularKeywords = [...popularKeywords];
    const popularKeywordIndex = clonedPopularKeywords.findIndex(({ keyword }) => keyword.toLowerCase() === targetKeyword.keyword.toLowerCase());

    if (popularKeywordIndex >= 0) {
      clonedPopularKeywords.splice(popularKeywordIndex, 1);
    } else {
      const clonedKeyword = { ...targetKeyword };
      clonedKeyword.isSelected = false;
      clonedPopularKeywords.push(clonedKeyword);
    }

    clonedPopularKeywords.forEach((keyword, index) => (keyword.rank = index + 1));

    rearrangeRankingOfPopularKeywords(clonedPopularKeywords);
    setPopularKeywords(clonedPopularKeywords);
  };

  const rearrangeRankingOfPopularKeywords = (keywords: IKeyword[]): void => {
    if (keywords === popularKeywords) {
      console.error('Do not manipulate state directly.');
      return;
    }

    keywords.forEach((k, i) => (k.rank = i + 1));
  };

  const onSortEnd = ({ newIndex, oldIndex }: { newIndex: number; oldIndex: number }) => {
    const clonedPopularKeywords = [...popularKeywords];

    const target = clonedPopularKeywords.splice(newIndex, 1, clonedPopularKeywords[oldIndex]);
    clonedPopularKeywords[oldIndex] = target[0];

    rearrangeRankingOfPopularKeywords(clonedPopularKeywords);
    setPopularKeywords(clonedPopularKeywords);
  };

  const handleChangePopularKeywordCheckbox = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event || !event.target) {
      return;
    }

    const key = event.target.value;
    const clonedPopularKeywords = [...popularKeywords];

    const targetKeyword = clonedPopularKeywords.find(({ keyword }) => key === keyword) as IKeyword;

    if (targetKeyword) {
      const currentSelection = targetKeyword.isSelected;
      targetKeyword.isSelected = !currentSelection;
    }

    setPopularKeywords(clonedPopularKeywords);
  };

  const isPopularKeywordQuotaExceeded = popularKeywords.length >= 10;

  return (
    <div>
      <Paper className={classes.wrapper}>
        <Grid container direction="row" justify="flex-start" alignItems="flex-start" spacing={5}>
          <Grid item xs={12}>
            <Grid container direction="row" justify="space-between" alignItems="center">
              <Typography variant="h6" component="h2">
                Popular Keyword
              </Typography>

              <SubmitControl
                mode={isEditing ? 'edit' : 'view'}
                onSubmitClick={handleClickApplyButton}
                onEditClick={handleClickEditButton}
                onCancelClick={handleClickCancelEditButton}
              />
            </Grid>
          </Grid>
          <Grid item className={classes.topSearchKeywordWrapper}>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Top 50 Search Keyword</Typography>
            </Grid>
            <Grid container direction="row" justify="space-between" alignItems="center">
              <Typography variant="body2" className={isPopularKeywordQuotaExceeded ? classes.maxKeywordDescEmphasized : classes.maxKeywordDesc}>
                * You can select up to 10 keywords.
              </Typography>
              <ButtonGroup variant="outlined" color="primary" size="small">
                <Button
                  className={`${classes.durationButton} ${duration === 7 && classes.selected}`}
                  onClick={handleClickLastDayWithDuration(7)}
                  disabled={isFetching}
                >
                  Last 7 days
                </Button>
                <Button
                  className={`${classes.durationButton} ${duration === 30 && classes.selected}`}
                  onClick={handleClickLastDayWithDuration(30)}
                  disabled={isFetching}
                >
                  Last 30 days
                </Button>
              </ButtonGroup>
            </Grid>
            <Grid item xs={12}>
              <Box className={classes.searchKeywordBox}>
                <Grid container>
                  {Object.entries(topSearchKeywordsDictionary).map(([, keywordObject]: [any, IKeyword], index) => (
                    <Grid item xs={6} key={`search-keyword-cell-${duration}-${index}`}>
                      <KeywordCell
                        keywordObject={keywordObject}
                        isEditing={isEditing}
                        onCheck={handleChangeTopSearchKeywordCheckbox}
                        isBlockedToSelect={isPopularKeywordQuotaExceeded}
                        isDisabled={isFetching}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>
          <Grid item className={classes.popularKeywordWrapper}>
            <Grid item xs={12}>
              <Grid container justify="space-between" alignItems="center">
                <Typography variant="subtitle2">Popular Keyword</Typography>
                <Button
                  className={classes.deleteButton}
                  onClick={handleClickDeleteButton}
                  disabled={popularKeywords.filter(({ isSelected }) => isSelected).length === 0}
                  style={{ visibility: isEditing ? 'visible' : 'hidden' }}
                >
                  Delete
                  <DeleteIcon />
                </Button>
              </Grid>

              <Grid container>
                <SortableBox className={classes.popularKeywordBox} lockAxis="y" useDragHandle={true} onSortEnd={onSortEnd}>
                  {popularKeywords.map((keywordObject, index) => (
                    <SortableKeywordCell
                      keywordObject={keywordObject}
                      border={true}
                      index={index}
                      key={`popular-keyword-cell-${duration}-${index}`}
                      isEditing={isEditing}
                      onCheck={handleChangePopularKeywordCheckbox}
                    />
                  ))}
                </SortableBox>
              </Grid>
            </Grid>

            <Grid container direction="row" alignItems="center" justify="flex-start" className={classes.addKeywordInput}>
              <TextField
                disabled={!isEditing || isPopularKeywordQuotaExceeded}
                placeholder="Add Keyword"
                value={addKeywordInputValue}
                onChange={handleChangeAddKeywordInput}
                onKeyPress={handleKeyPressAddKeywordInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={handleClickClearAddKeywordInput}
                        disableFocusRipple
                        style={{ visibility: addKeywordInputValue.length > 0 ? 'visible' : 'hidden' }}
                        disabled={isPopularKeywordQuotaExceeded}
                      >
                        <Clear fontSize="small" className={classes.clearButton} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton disabled={!isEditing || isPopularKeywordQuotaExceeded} onClick={() => addPopularKeyword(addKeywordInputValue)}>
                <AddBox />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      <Paper className={classes.wrapper}>
        <KeywordManagement<ILateNightKeyword>
          title="Late night glossary"
          fetchFactory={getLateNightKeywords}
          saveFactory={addLateNightKeyword}
          deleteFactory={deleteLateNightKeywordById}
        />
      </Paper>
    </div>
  );
};

const SortableBox = sortableContainer((props: any) => {
  const { children, className } = props;

  return <Box className={className}>{children}</Box>;
});

const DragHandle = sortableHandle(() => <VerticalEllipsisIcon />);

const SortableKeywordCell: React.ComponentClass<IKeywordCellProps & SortableElementProps> = sortableElement((props: IKeywordCellProps) => {
  const { keywordObject, border, isEditing, onCheck } = props;
  return <KeywordCell keywordObject={keywordObject} border={border} isEditing={isEditing} isDraggable={true} onCheck={onCheck} />;
});

const KeywordCell = (props: IKeywordCellProps): JSX.Element => {
  const classes = useStyles();

  const { keywordObject, border, isEditing, isDraggable, onCheck, isBlockedToSelect, isDisabled } = props;
  const { rank, keyword, isSelected } = keywordObject;

  return (
    <Grid item xs={12}>
      <Box className={`${classes.keywordCell} ${border && classes.keywordCellBorder} ${isEditing && isDraggable && classes.keywordCellEditing}`}>
        <FormGroup
          classes={{
            root: classes.keywordFormGroup,
          }}
        >
          {!isEditing && <span className={isDisabled ? classes.keywordDisabled : undefined}>{`${rank}. ${keyword}`}</span>}
          {isEditing && (
            <React.Fragment>
              {isDraggable && <DragHandle />}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSelected}
                    onChange={onCheck}
                    value={keyword}
                    color="primary"
                    disabled={isDisabled || (!isSelected && isBlockedToSelect)}
                  />
                }
                classes={{
                  label: classes.keywordFormLabel,
                }}
                label={`${rank}. ${keyword}`}
              />
            </React.Fragment>
          )}
        </FormGroup>
      </Box>
    </Grid>
  );
};

export default SearchFeature;
